/**
 * Step — Tag products (BLU-1371, BLU-1373, client variant). Sits straight
 * after Claim location. Three things, saved together in ONE call through the
 * existing product sync settings mutation (`useProductSyncSettingsBuffer().savePatch`):
 *
 *  1. The product sync tag. Read-only here with a Copy button: the org's saved
 *     tag if it already has one (onboarding never overwrites an existing gate),
 *     else `PrintRun`. Only products carrying it are scanned, so the steps that
 *     follow see only tagged products.
 *  2. What the client sells (`sells`): ready-made designs, custom, or both.
 *     Both adds the Custom or ready-made step right after this one.
 *  3. Whose garments the client prints on (`blankSource`): the 3PL's, its own,
 *     or both. With the 3PL's garments the step model hides code tables, blank
 *     products and blank stock. The 3PL's and the both cards are enabled only
 *     when an active junction has the 3PL's consent (`client_blank_supply_enabled`),
 *     the same gate Product sync's garment picker uses, so the client never
 *     picks a path it cannot use.
 *
 * A save that would change how an org's products get SKUs asks first when the
 * org may already have synced products (`SkuChangeConfirmDialog`); any other
 * save goes straight through.
 *
 * Completion is server-driven (tag + both answers saved). A live client that
 * synced products before this step existed reads it as Skipped (see `steps.ts`).
 * The body (and so the settings read) mounts only while the step is selected
 * and not locked. Both questions are `OptionCard` radios (handover 4.3); Save
 * is the pane footer's primary and its toast names this step (never the code
 * tables message).
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import {
  customDecisionChanged,
  type BlankSource,
  type SellsKind,
} from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { useRole } from '@/hooks/use-role';
import { CopyValue } from '@/features/organization/components/shopify-connection/CopyValue';
import { useProductSyncSettingsBuffer } from '@/features/product-sync/hooks/useProductSyncSettingsBuffer';
import { ONBOARDING_SYNC_TAG } from '../../lib/steps';
import { useSavedHold } from '../../hooks/useSavedHold';
import { SkuChangeConfirmDialog } from '../SkuChangeConfirmDialog';
import { StepPane } from '../shell/StepPane';
import { useStepChrome } from '../shell/StepChrome';
import { OptionCard } from '../shell/OptionCard';
import { SavePrimaryButton } from '../shell/SavePrimaryButton';

interface StepSyncTagProps {
  /** Complete only through already-synced products (a client from before this step). */
  skipped: boolean;
  /** The org's saved sync tag (empty string = none). */
  savedTag: string;
  /** What the client said it sells (`null` = never answered). */
  sells: SellsKind | null;
  /** The org's saved garment choice (`null` = never chose). */
  blankSource: BlankSource | null;
  /** At least one active junction lets this client print on the 3PL's garments. */
  supplyAvailable: boolean;
  /** The org may already have synced products: a save that changes SKUs asks first. */
  hasSyncedProducts: boolean;
  /** Refetch the onboarding status after a successful save. */
  onSaved: () => void;
}

/** Garment cards, in display order. */
const GARMENT_CHOICES: readonly BlankSource[] = ['supply', 'own', 'both'];
/** What-you-sell cards, in display order. */
const SELLS_CHOICES: readonly SellsKind[] = ['readyMade', 'custom', 'both'];

const GARMENT_SUMMARY_KEY: Record<BlankSource, string> = {
  supply: 'stepSyncTag.summarySupply',
  own: 'stepSyncTag.summaryOwn',
  both: 'stepSyncTag.summaryBoth',
};

const SELLS_SUMMARY_KEY: Record<SellsKind, string> = {
  readyMade: 'stepSyncTag.summarySells.readyMade',
  custom: 'stepSyncTag.summarySells.custom',
  both: 'stepSyncTag.summarySells.both',
};

/** Card copy per answer, spelled out so every key is a literal the i18n check can see. */
const SELLS_CARD_KEYS: Record<SellsKind, { label: string; body: string }> = {
  readyMade: { label: 'stepSyncTag.sells.readyMade.label', body: 'stepSyncTag.sells.readyMade.body' },
  custom: { label: 'stepSyncTag.sells.custom.label', body: 'stepSyncTag.sells.custom.body' },
  both: { label: 'stepSyncTag.sells.both.label', body: 'stepSyncTag.sells.both.body' },
};

const GARMENT_CARD_KEYS: Record<BlankSource, { label: string; body: string }> = {
  supply: { label: 'stepSyncTag.supply.label', body: 'stepSyncTag.supply.body' },
  own: { label: 'stepSyncTag.own.label', body: 'stepSyncTag.own.body' },
  both: { label: 'stepSyncTag.both.label', body: 'stepSyncTag.both.body' },
};

/** The two garment options that use the 3PL's garments (gated on its consent). */
function usesSupply(option: BlankSource): boolean {
  return option === 'supply' || option === 'both';
}

interface PaneFrame {
  title: string;
  description: string;
  summary: ReactNode;
}

export function StepSyncTag({
  skipped,
  savedTag,
  sells,
  blankSource,
  supplyAvailable,
  hasSyncedProducts,
  onSaved,
}: StepSyncTagProps) {
  const { t } = useTranslation('onboarding');
  const { state } = useStepChrome();

  const summary =
    savedTag && blankSource && sells ? (
      <>
        <span>{t(GARMENT_SUMMARY_KEY[blankSource], { tag: savedTag })}</span>{' '}
        <span>{t(SELLS_SUMMARY_KEY[sells])}</span>
      </>
    ) : skipped ? (
      t('stepSyncTag.summaryLegacy')
    ) : undefined;

  const frame: PaneFrame = {
    title: t('stepSyncTag.title'),
    description: t('stepSyncTag.description'),
    summary,
  };

  // A locked pane shows only its frame: the settings read waits for its turn.
  if (state === 'locked') return <StepPane {...frame} />;

  return (
    <StepSyncTagBody
      frame={frame}
      savedTag={savedTag}
      sells={sells}
      blankSource={blankSource}
      supplyAvailable={supplyAvailable}
      hasSyncedProducts={hasSyncedProducts}
      onSaved={onSaved}
    />
  );
}

/** The step body: its own settings buffer, mounted only while the step is open. */
function StepSyncTagBody({
  frame,
  savedTag,
  sells,
  blankSource,
  supplyAvailable,
  hasSyncedProducts,
  onSaved,
}: { frame: PaneFrame } & Pick<
  StepSyncTagProps,
  'savedTag' | 'sells' | 'blankSource' | 'supplyAvailable' | 'hasSyncedProducts' | 'onSaved'
>) {
  const { t } = useTranslation('onboarding');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const buffer = useProductSyncSettingsBuffer();
  const hold = useSavedHold();
  const sellsLabelId = useId();
  const garmentLabelId = useId();
  const hintId = useId();
  const groupName = useId();
  const saveRef = useRef<HTMLButtonElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const shownTag = savedTag || ONBOARDING_SYNC_TAG;

  // Local answers start from the saved ones and follow them when the status
  // refetches (a save here, or a change made elsewhere).
  const [sellsChoice, setSellsChoice] = useState<SellsKind | null>(sells);
  useEffect(() => {
    setSellsChoice(sells);
  }, [sells]);
  const [choice, setChoice] = useState<BlankSource | null>(blankSource);
  useEffect(() => {
    setChoice(blankSource);
  }, [blankSource]);

  const unchanged = shownTag === savedTag && choice === blankSource && sellsChoice === sells;
  const unanswered = choice === null || sellsChoice === null;
  const saveDisabled =
    !admin || unanswered || buffer.isLoading || buffer.isError || buffer.isSaving || unchanged;
  const controlsDisabled = !admin || buffer.isSaving;

  const save = () => {
    if (choice === null || sellsChoice === null) return;
    buffer.savePatch(
      { syncTag: shownTag, sells: sellsChoice, blankSource: choice },
      {
        onSuccess: () => {
          hold.markSaved();
          onSaved();
        },
        successMessage: t('stepSyncTag.saved'),
      },
    );
  };

  const onSave = () => {
    if (choice === null || sellsChoice === null) return;
    // Only `sells` here can change how products get SKUs; ask first when the
    // org may already have synced products.
    const next = { ...buffer.serverSettings, sells: sellsChoice };
    if (hasSyncedProducts && customDecisionChanged(buffer.serverSettings, next)) {
      setConfirmOpen(true);
      return;
    }
    save();
  };

  // The footer hint says why Save is not offered yet (read with the button).
  const hint = !admin ? t('stepSyncTag.adminOnly') : unanswered ? t('stepSyncTag.answerBoth') : undefined;

  return (
    <StepPane
      {...frame}
      footerHint={hint && <span id={hintId}>{hint}</span>}
      primary={
        <SavePrimaryButton
          ref={saveRef}
          phase={hold.phase(buffer.isSaving)}
          disabled={saveDisabled}
          onClick={onSave}
          describedBy={hint ? hintId : undefined}
        >
          <Save className="h-3.5 w-3.5" aria-hidden="true" />
          {t('stepSyncTag.save')}
        </SavePrimaryButton>
      }
    >
      <div className="space-y-6">
        <CopyValue
          label={t('stepSyncTag.tagLabel')}
          value={shownTag}
          hint={<p className="text-xs text-muted-foreground">{t('stepSyncTag.tagHint')}</p>}
        />

        <div className="space-y-2.5">
          <p id={sellsLabelId} className="text-sm font-medium text-foreground">
            {t('stepSyncTag.sellsLabel')}
          </p>
          <div role="radiogroup" aria-labelledby={sellsLabelId} className="grid gap-2.5 sm:grid-cols-3">
            {SELLS_CHOICES.map((option) => (
              <OptionCard
                key={option}
                name={`${groupName}-sells`}
                value={option}
                checked={sellsChoice === option}
                onChange={() => setSellsChoice(option)}
                label={t(SELLS_CARD_KEYS[option].label)}
                body={t(SELLS_CARD_KEYS[option].body)}
                disabled={controlsDisabled}
              />
            ))}
          </div>
        </div>

        <div className="space-y-2.5">
          <p id={garmentLabelId} className="text-sm font-medium text-foreground">
            {t('stepSyncTag.choiceLabel')}
          </p>
          <div role="radiogroup" aria-labelledby={garmentLabelId} className="grid gap-2.5 sm:grid-cols-3">
            {GARMENT_CHOICES.map((option) => {
              const unavailable = usesSupply(option) && !supplyAvailable;
              // A card that uses the 3PL's garments stays selectable when it is
              // already the saved choice, so a client is never stuck showing a
              // disabled answer.
              const lockedOut = unavailable && blankSource !== option;
              return (
                <OptionCard
                  key={option}
                  name={`${groupName}-garments`}
                  value={option}
                  checked={choice === option}
                  onChange={() => setChoice(option)}
                  label={t(GARMENT_CARD_KEYS[option].label)}
                  body={
                    unavailable && !lockedOut ? (
                      <>
                        {t(GARMENT_CARD_KEYS[option].body)}
                        <span className="mt-1 block text-amber-700">{t('stepSyncTag.supply.unavailable')}</span>
                      </>
                    ) : (
                      t(GARMENT_CARD_KEYS[option].body)
                    )
                  }
                  disabled={controlsDisabled || lockedOut}
                  disabledReason={unavailable ? t('stepSyncTag.supply.unavailable') : undefined}
                />
              );
            })}
          </div>
        </div>

        {buffer.isError && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{t('stepSyncTag.loadError')}</p>
            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs" onClick={buffer.refetch}>
              {t('states.retry')}
            </Button>
          </div>
        )}
      </div>

      <SkuChangeConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={save}
        returnFocusRef={saveRef}
      />
    </StepPane>
  );
}
